import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
} from "@mui/material";
import { Save, Lock } from "@mui/icons-material";
import axios from "axios";
import logger from "../../utils/logger";

const Settings = () => {
  const [settings, setSettings] = useState({
    site_name: "",
    site_url: "",
    admin_email: "",
    smtp_host: "",
    smtp_port: "",
    smtp_username: "",
    smtp_password: "",
    backup_enabled: false,
    backup_frequency: "daily",
    maintenance_mode: false,
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem("superAdminToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/super-admin/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.data) {
        setSettings(response.data.data);
      }
    } catch (error) {
      logger.error("Error fetching settings:", error);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    const token = localStorage.getItem("superAdminToken");

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/super-admin/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("تم حفظ الإعدادات بنجاح", "success");
    } catch (error) {
      logger.error("Error saving settings:", error);
      showSnackbar("فشل حفظ الإعدادات", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      showSnackbar("كلمات المرور الجديدة غير متطابقة", "error");
      return;
    }

    if (passwordData.new_password.length < 8) {
      showSnackbar("كلمة المرور يجب أن تكون 8 أحرف على الأقل", "error");
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("superAdminToken");

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/super-admin/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar("تم تغيير كلمة المرور بنجاح", "success");
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (error) {
      logger.error("Error changing password:", error);
      showSnackbar(
        error.response?.data?.message || "فشل تغيير كلمة المرور",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        الإعدادات
      </Typography>

      {/* General Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          الإعدادات العامة
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="اسم الموقع"
              value={settings.site_name}
              onChange={(e) =>
                setSettings({ ...settings, site_name: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="رابط الموقع"
              value={settings.site_url}
              onChange={(e) =>
                setSettings({ ...settings, site_url: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="البريد الإلكتروني للمسؤول"
              type="email"
              value={settings.admin_email}
              onChange={(e) =>
                setSettings({ ...settings, admin_email: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.maintenance_mode}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      maintenance_mode: e.target.checked,
                    })
                  }
                />
              }
              label="وضع الصيانة"
            />
          </Grid>
        </Grid>

        <Box mt={3}>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveSettings}
            disabled={loading}
          >
            حفظ الإعدادات العامة
          </Button>
        </Box>
      </Paper>

      {/* Email Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          إعدادات البريد الإلكتروني (SMTP)
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Alert severity="info" sx={{ mb: 3 }}>
          هذه الإعدادات اختيارية. يمكنك تكوينها لإرسال إشعارات البريد
          الإلكتروني.
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="خادم SMTP"
              placeholder="smtp.gmail.com"
              value={settings.smtp_host}
              onChange={(e) =>
                setSettings({ ...settings, smtp_host: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="منفذ SMTP"
              placeholder="587"
              value={settings.smtp_port}
              onChange={(e) =>
                setSettings({ ...settings, smtp_port: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="اسم المستخدم"
              value={settings.smtp_username}
              onChange={(e) =>
                setSettings({ ...settings, smtp_username: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="كلمة المرور"
              type="password"
              value={settings.smtp_password}
              onChange={(e) =>
                setSettings({ ...settings, smtp_password: e.target.value })
              }
            />
          </Grid>
        </Grid>

        <Box mt={3}>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveSettings}
            disabled={loading}
          >
            حفظ إعدادات البريد
          </Button>
        </Box>
      </Paper>

      {/* Backup Settings */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          إعدادات النسخ الاحتياطي
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.backup_enabled}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      backup_enabled: e.target.checked,
                    })
                  }
                />
              }
              label="تفعيل النسخ الاحتياطي التلقائي"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="تكرار النسخ الاحتياطي"
              value={settings.backup_frequency}
              onChange={(e) =>
                setSettings({ ...settings, backup_frequency: e.target.value })
              }
              SelectProps={{ native: true }}
              disabled={!settings.backup_enabled}
            >
              <option value="daily">يومي</option>
              <option value="weekly">أسبوعي</option>
              <option value="monthly">شهري</option>
            </TextField>
          </Grid>
        </Grid>

        <Box mt={3}>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveSettings}
            disabled={loading}
          >
            حفظ إعدادات النسخ الاحتياطي
          </Button>
        </Box>
      </Paper>

      {/* Change Password */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          تغيير كلمة المرور
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="كلمة المرور الحالية"
              type="password"
              value={passwordData.current_password}
              onChange={(e) =>
                setPasswordData({
                  ...passwordData,
                  current_password: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="كلمة المرور الجديدة"
              type="password"
              value={passwordData.new_password}
              onChange={(e) =>
                setPasswordData({
                  ...passwordData,
                  new_password: e.target.value,
                })
              }
              helperText="8 أحرف على الأقل"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="تأكيد كلمة المرور الجديدة"
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) =>
                setPasswordData({
                  ...passwordData,
                  confirm_password: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>

        <Box mt={3}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Lock />}
            onClick={handleChangePassword}
            disabled={
              loading ||
              !passwordData.current_password ||
              !passwordData.new_password
            }
          >
            تغيير كلمة المرور
          </Button>
        </Box>
      </Paper>

      {/* System Info */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            معلومات النظام
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                إصدار النظام:
              </Typography>
              <Typography variant="body1">2.5.0</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                آخر تحديث:
              </Typography>
              <Typography variant="body1">
                {new Date().toLocaleDateString("ar-IQ")}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
